import React from 'react';
import { Modal } from '@/components/molecules';
import { Icon, Badge } from '@/components/atoms';
import { useVariantBySku } from '@/hooks/useErpApi';
import type { ProductVariantResponse } from '@kisanlink/erp-service';

interface VariantViewModalProps {
  variant: ProductVariantResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

export const VariantViewModal: React.FC<VariantViewModalProps> = ({
  variant,
  isOpen,
  onClose,
}) => {
  const sku = variant?.sku || null;
  const { data: variantData, isLoading, error } = useVariantBySku(sku);

  const variantInfo = (variantData as any)?.data || variant;

  if (!isOpen || !variant) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Variant Details" size="lg">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-primary-600" />
        </div>
      )}

      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-4">
          <p className="text-error-700">Failed to load variant details: {error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      )}

      {variantInfo && !isLoading && (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-600">Variant Name</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.variant_name}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">SKU</label>
              <p className="text-sm text-neutral-900 font-mono mt-1">{variantInfo.sku || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Brand Name</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.brand_name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Barcode</label>
              <p className="text-sm text-neutral-900 font-mono mt-1">{variantInfo.barcode || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Pack Size</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.pack_size}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Quantity</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.quantity}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">HSN Code</label>
              <p className="text-sm text-neutral-900 font-mono mt-1">{variantInfo.hsn_code || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">GST Rate</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.gst_rate ? `${variantInfo.gst_rate}%` : 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Status</label>
              <div className="mt-1">
                <Badge variant={variantInfo.is_active ? 'success' : 'secondary'} size="sm">
                  {variantInfo.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Description */}
          {variantInfo.description && (
            <div>
              <label className="text-xs font-medium text-neutral-600">Description</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.description}</p>
            </div>
          )}

          {/* Dosage Instructions */}
          {variantInfo.dosage_instructions && (
            <div>
              <label className="text-xs font-medium text-neutral-600">Dosage Instructions</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.dosage_instructions}</p>
            </div>
          )}

          {/* Usage Details */}
          {variantInfo.usage_details && (
            <div>
              <label className="text-xs font-medium text-neutral-600">Usage Details</label>
              <p className="text-sm text-neutral-900 mt-1">{variantInfo.usage_details}</p>
            </div>
          )}

          {/* Images */}
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-2 block">Images</label>
            {variantInfo.image_urls && variantInfo.image_urls.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {variantInfo.image_urls.map((url: string, index: number) => (
                  <div
                    key={index}
                    className="aspect-square bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200 hover:border-primary-300 transition-colors cursor-pointer"
                    onClick={() => window.open(url, '_blank')}
                    title="Click to view full size"
                  >
                    <img
                      src={url}
                      alt={`${variantInfo.variant_name} image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Hide broken images and show placeholder
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.parentElement?.querySelector('.image-placeholder');
                        if (placeholder) {
                          (placeholder as HTMLElement).style.display = 'flex';
                        }
                      }}
                    />
                    <div className="image-placeholder hidden w-full h-full items-center justify-center">
                      <Icon name="Image" size={32} className="text-neutral-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-neutral-500 italic py-4">No images available</div>
            )}
          </div>

          {/* Prices */}
          {variantInfo.prices && variantInfo.prices.length > 0 && (
            <div>
              <label className="text-xs font-medium text-neutral-600 mb-2 block">Prices</label>
              <div className="space-y-2">
                {variantInfo.prices.map((price: any) => (
                  <div key={price.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-neutral-900 capitalize">{price.price_type}</p>
                      {price.effective_from && (
                        <p className="text-xs text-neutral-600">
                          Effective from: {new Date(price.effective_from).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-neutral-900">₹{price.price.toFixed(2)}</p>
                      <Badge variant={price.is_active ? 'success' : 'secondary'} size="sm">
                        {price.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
            <div>
              <label className="text-xs font-medium text-neutral-600">Created At</label>
              <p className="text-sm text-neutral-900 mt-1">
                {new Date(variantInfo.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Updated At</label>
              <p className="text-sm text-neutral-900 mt-1">
                {new Date(variantInfo.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

